import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/**
 * Wire types mirror `packages/contracts/schemas/domain/shifts.ts`
 * + the BOS shape extensions (joined cashier + terminal in detail).
 */

export type ShiftState =
  | 'open'
  | 'active'
  | 'paused'
  | 'closing'
  | 'closed'
  | 'reconciled'
  | 'abnormal_close';

/** Keep this tuple aligned with the shared wire contract. Deriving the type
 * from the rendered/filterable values prevents the UI from accepting a kind
 * in API data while quietly making it impossible to select in the report. */
export const CASH_MOVEMENT_KINDS = [
  'drop',
  'pickup',
  'bank_deposit',
  'change_order',
  'paid_in',
  'paid_out',
  'change_in',
  'change_out',
  'revenue_out',
] as const;
export type CashMovementKind = (typeof CASH_MOVEMENT_KINDS)[number];

export type ShiftSummary = {
  /** YUKI-258: resolved from the shift's receipts; null when unknown or mixed. */
  currencyCode: string | null;
  id: string;
  clientUuid: string;
  cashierId: string;
  terminalId: string;
  state: ShiftState;
  openedAt: string;
  openingCashMinor: string;
  closedAt: string | null;
  closingCashCountedMinor: string | null;
  closingCashExpectedMinor: string | null;
  varianceMinor: string | null;
  varianceReasonCode: string | null;
  zReportRef: string | null;
  reconciledAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CashMovementRecord = {
  /** YUKI-258: the owning shift's currency; null when unknown or mixed. */
  currencyCode: string | null;
  id: string;
  clientUuid: string;
  shiftId: string;
  terminalId: string;
  kind: CashMovementKind;
  amountMinor: string;
  reasonCode: string;
  notes: string | null;
  actorCashierId: string | null;
  supervisorApproverId: string | null;
  pairedMovementId: string | null;
  occurredAt: string;
  createdAt: string;
};

export type ShiftReconciliationAdjustment = {
  id: string;
  shiftId: string;
  amountMinor: string;
  reason: string;
  actorUserId: string | null;
  createdAt: string;
};

/** A drawer count filed against the shift by the till (VL-250). */
export type CashCountRecord = {
  id: string;
  clientUuid: string;
  shiftId: string;
  terminalId: string;
  currency: string;
  expectedMinor: string;
  countedMinor: string;
  /** Counted minus expected; negative is a shortage. */
  differenceMinor: string;
  denominations: Array<{ denominationMinor: string; count: number }>;
  countedByCashierId: string | null;
  countedAt: string;
  createdAt: string;
};

export type ShiftDetail = ShiftSummary & {
  cashier: { id: string; employeeCode: string; displayName: string };
  terminal: { id: string; code: string; displayName: string };
  cashMovements: CashMovementRecord[];
  /** Optional: a Back Office build without VL-250 does not send it. */
  cashCounts?: CashCountRecord[];
  reconciliationAdjustments: ShiftReconciliationAdjustment[];
};

export type ShiftListResponse = {
  shifts: ShiftSummary[];
  page: number;
  pageSize: number;
  total: number;
};

export type CashMovementListResponse = {
  movements: CashMovementRecord[];
  page: number;
  pageSize: number;
  total: number;
};

export type TurnoverShiftRow = {
  shiftId: string;
  /** YUKI-258: null when unknown or mixed. */
  currencyCode: string | null;
  cashier: { id: string; employeeCode: string; displayName: string };
  terminal: { id: string; code: string; displayName: string };
  state: ShiftState;
  openedAt: string;
  closedAt: string | null;
  openingCashMinor: string;
  closingCashCountedMinor: string | null;
  closingCashExpectedMinor: string | null;
  varianceMinor: string | null;
  dropsMinor: string;
  pickupsMinor: string;
};

export type TurnoverReport = {
  date: string;
  shifts: TurnoverShiftRow[];
  rollup: {
    /** YUKI-258: named only when every shift in the window agrees. */
    currencyCode: string | null;
    shiftCount: number;
    openingCashMinor: string;
    closingCashCountedMinor: string;
    closingCashExpectedMinor: string;
    varianceMinor: string;
  };
};

export type ShiftListQuery = {
  page?: number;
  pageSize?: number;
  terminalId?: string;
  cashierId?: string;
  state?: ShiftState | readonly ShiftState[];
};

const LIST_KEY = ['shifts'] as const;
const ONE_KEY = (id: string) => ['shifts', id] as const;
const MOVES_KEY = ['cash-movements'] as const;
const TURNOVER_KEY = ['reports', 'turnover'] as const;

export function useShifts(query: ShiftListQuery = {}) {
  return useQuery({
    queryKey: [...LIST_KEY, query] as const,
    queryFn: async (): Promise<ShiftListResponse> => {
      const params: Record<string, string | number> = {};
      if (query.page !== undefined) params.page = query.page;
      if (query.pageSize !== undefined) params.pageSize = query.pageSize;
      if (query.terminalId !== undefined) params['filter[terminalId]'] = query.terminalId;
      if (query.cashierId !== undefined) params['filter[cashierId]'] = query.cashierId;
      if (query.state !== undefined) {
        params['filter[state]'] =
          typeof query.state === 'string' ? query.state : query.state.join(',');
      }
      const res = await apiClient.get<ShiftListResponse>('/shifts', { params });
      return res.data;
    },
  });
}

export function useShift(id: string | undefined) {
  return useQuery({
    queryKey: id === undefined ? ['shifts', '__noop'] : ONE_KEY(id),
    enabled: id !== undefined,
    queryFn: async (): Promise<ShiftDetail> => {
      const res = await apiClient.get<ShiftDetail>(`/shifts/${id}`);
      return res.data;
    },
  });
}

export function useCashMovements(filter: { kind?: CashMovementKind } = {}) {
  return useQuery({
    queryKey: [...MOVES_KEY, filter] as const,
    queryFn: async (): Promise<CashMovementListResponse> => {
      const params: Record<string, string> = {};
      if (filter.kind !== undefined) params['filter[kind]'] = filter.kind;
      const res = await apiClient.get<CashMovementListResponse>('/cash-movements', { params });
      return res.data;
    },
  });
}

export function useReconcileShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      body:
        | { expectedAction: 'accept'; reason: string }
        | { expectedAction: 'adjust'; adjustmentMinor: string; reason: string };
    }): Promise<ShiftSummary> => {
      const res = await apiClient.post<ShiftSummary>(`/shifts/${vars.id}/reconcile`, vars.body);
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(vars.id) });
      qc.invalidateQueries({ queryKey: TURNOVER_KEY });
    },
  });
}

export function useTurnover(date: string | undefined, window?: { from: string; to: string }) {
  return useQuery({
    queryKey: [...TURNOVER_KEY, date ?? 'today', window?.from ?? '', window?.to ?? ''] as const,
    queryFn: async (): Promise<TurnoverReport> => {
      const res = await apiClient.get<TurnoverReport>('/reports/turnover', {
        params: {
          ...(date === undefined ? {} : { date }),
          ...(window === undefined ? {} : { from: window.from, to: window.to }),
        },
      });
      return res.data;
    },
  });
}

/**
 * Format minor-unit BigInt-string as a localised money string. Cents-
 * scale (2-digit) — the smallest unit for both EUR and UAH in MVP
 * deployments. Country pack TODO if non-2-digit currencies enter
 * the mix (e.g. JPY).
 */
/**
 * YUKI-258: the currency is REQUIRED, and deliberately so.
 *
 * This defaulted to `'EUR'`, so every caller that had no currency to hand —
 * the shifts list, the shift detail, the cash-movements report — printed euro
 * signs over a Ukrainian operator's hryvnia. Removing the default turns each of
 * those into a compiler error rather than a silent wrong glyph, which is how
 * they were all found.
 *
 * Pass `null` when the currency genuinely is not known: that prints the amount
 * bare. An unmarked number is honest; the wrong symbol on a cash figure an
 * operator reconciles a drawer against is not.
 */
export function formatMinor(minor: string | null, currency: string | null): string {
  if (minor === null) return '—';
  const cents = BigInt(minor);
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  const whole = abs / 100n;
  const frac = abs % 100n;
  const fracStr = frac.toString().padStart(2, '0');
  const sign = negative ? '-' : '';
  const symbol = currency === 'EUR' ? '€' : currency === 'UAH' ? '₴' : '';
  // An unrecognised but present currency keeps its code beside the digits, so
  // a third currency entering the estate is visible rather than silently bare.
  const suffix = currency !== null && currency !== '' && symbol === '' ? ` ${currency}` : '';
  return `${sign}${symbol}${whole}.${fracStr}${suffix}`;
}

// ---------- Display helpers ----------

/**
 * Badge words for the two shift enums, with their catalogue keys alongside.
 *
 * Both badges used to render the raw wire token — `shift.state` as-is and
 * `kind.replace('_', ' ')` — so the English here is byte-identical to what the
 * token produced, lower case included. A caller resolves a label as
 * `t(SHIFT_STATE_LABEL_KEY[s], SHIFT_STATE_LABEL[s])`; the keys are literals
 * because the parity guard reads source for literal keys.
 *
 * The state rows are the ones the shifts sweep already shipped, so the badge and
 * the list filter now speak with one voice instead of two.
 */
export const SHIFT_STATE_LABEL: Record<ShiftState, string> = {
  open: 'open',
  active: 'active',
  paused: 'paused',
  closing: 'closing',
  closed: 'closed',
  reconciled: 'reconciled',
  abnormal_close: 'abnormal close',
};

export const SHIFT_STATE_LABEL_KEY: Record<ShiftState, string> = {
  open: 'shifts.state.open',
  active: 'shifts.state.active',
  paused: 'shifts.state.paused',
  closing: 'shifts.state.closing',
  closed: 'shifts.state.closed',
  reconciled: 'shifts.state.reconciled',
  abnormal_close: 'shifts.state.abnormalClose',
};

export const CASH_MOVEMENT_KIND_LABEL: Record<CashMovementKind, string> = {
  drop: 'drop',
  pickup: 'pickup',
  bank_deposit: 'bank deposit',
  change_order: 'change order',
  paid_in: 'paid in',
  paid_out: 'paid out',
  change_in: 'change in',
  change_out: 'change out',
  revenue_out: 'revenue out',
};

export const CASH_MOVEMENT_KIND_LABEL_KEY: Record<CashMovementKind, string> = {
  drop: 'shifts.vocab.kindDrop',
  pickup: 'shifts.vocab.kindPickup',
  bank_deposit: 'shifts.vocab.kindBankDeposit',
  change_order: 'shifts.vocab.kindChangeOrder',
  paid_in: 'shifts.vocab.kindPaidIn',
  paid_out: 'shifts.vocab.kindPaidOut',
  change_in: 'shifts.vocab.kindChangeIn',
  change_out: 'shifts.vocab.kindChangeOut',
  revenue_out: 'shifts.vocab.kindRevenueOut',
};
